import React, { useEffect, useState } from 'react'
import { t } from '../i18n'

/**
 * Barra de modo Comparar (+ Hanging MVHP): guía, chip salir, protocolo activo.
 * Se renderiza encima del grid de viewports (no overlay sobre las imágenes).
 */
function CompareModeGuide({
  open,
  compareMode,
  guideOpen,
  onDismiss,
  onExit,
  guideText,
  hangingEnabled,
  hangingActiveId,
  hangingDetached,
  hangingLabel,
  hangingProtocols,
  hangingAutoApply,
  onSelectHanging,
  onToggleHangingAutoApply,
  onOpenHangingEditor,
}) {
  const [tipVisible, setTipVisible] = useState(!!guideOpen)

  useEffect(() => {
    if (!open || !guideOpen) {
      setTipVisible(false)
      return undefined
    }
    setTipVisible(true)
    const timer = setTimeout(() => {
      setTipVisible(false)
      if (typeof onDismiss === 'function') onDismiss()
    }, 6000)
    return () => clearTimeout(timer)
  }, [open, guideOpen, onDismiss])

  if (!open) return null

  const showTip = !!guideOpen && tipVisible && !!compareMode
  const protocols = Array.isArray(hangingProtocols) ? hangingProtocols : []
  const showHanging = !!hangingEnabled
  const showCompareExit = !!compareMode

  return (
    <div
      className="udv-compare-mode-bar"
      role="status"
      style={{
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
        width: '100%',
        boxSizing: 'border-box',
        borderBottom: '1px solid rgba(100, 181, 246, 0.35)',
        background: 'rgba(22, 32, 48, 0.96)',
        zIndex: 5,
      }}
    >
      {showTip ? (
        <div
          className="udv-compare-mode-guide"
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid rgba(196, 165, 116, 0.28)',
            background: 'rgba(28, 34, 44, 0.98)',
            color: '#e8e6e3',
            fontSize: 12,
            lineHeight: 1.35,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <span style={{ flex: 1 }}>
            {guideText || t('relatedStudies.compareGuide')}
          </span>
          <button
            type="button"
            onClick={() => {
              setTipVisible(false)
              if (typeof onDismiss === 'function') onDismiss()
            }}
            aria-label={t('common.close')}
            style={{
              flex: '0 0 auto',
              border: 'none',
              background: 'transparent',
              color: '#c4a574',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      ) : null}

      <div
        className="udv-compare-mode-bar-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 10,
          padding: '6px 10px',
          minHeight: 36,
          boxSizing: 'border-box',
        }}
      >
        {showHanging ? (
          <div
            className="udv-hanging-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px 4px 10px',
              borderRadius: 999,
              border: hangingDetached
                ? '1px solid rgba(180,180,180,0.45)'
                : '1px solid rgba(129, 199, 132, 0.65)',
              background: hangingDetached
                ? 'rgba(50,50,55,0.9)'
                : 'rgba(30, 70, 40, 0.92)',
              color: '#e8f5e9',
              fontSize: 11,
              fontWeight: 700,
              maxWidth: '100%',
            }}
            title={t('hanging.chipTitle')}
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              {hangingDetached || !hangingActiveId
                ? t('hanging.manual')
                : t('hanging.chip', {
                    label: hangingLabel || hangingActiveId || '—',
                  })}
            </span>
            <select
              aria-label={t('hanging.selectAria')}
              value={hangingActiveId || ''}
              onChange={(e) => {
                const v = e.target.value
                if (typeof onSelectHanging === 'function') onSelectHanging(v || null)
              }}
              style={{
                maxWidth: 160,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                padding: '2px 4px',
              }}
            >
              <option value="">{t('hanging.none')}</option>
              {protocols.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                if (typeof onToggleHangingAutoApply === 'function') {
                  onToggleHangingAutoApply()
                }
              }}
              title={
                hangingAutoApply
                  ? t('hanging.autoApplyOnHint')
                  : t('hanging.autoApplyOffHint')
              }
              style={{
                border: 'none',
                background: 'transparent',
                color: hangingAutoApply ? '#a5d6a7' : '#9e9e9e',
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: 700,
                padding: '0 4px',
              }}
            >
              {hangingAutoApply ? 'AUTO' : 'MAN'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (typeof onOpenHangingEditor === 'function') {
                  onOpenHangingEditor()
                }
              }}
              title={t('hanging.editorOpenHint')}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#90caf9',
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: 700,
                padding: '0 4px',
              }}
            >
              {t('hanging.editorOpen')}
            </button>
          </div>
        ) : null}

        {showCompareExit ? (
          <button
            type="button"
            className="udv-compare-mode-exit-chip"
            onClick={() => {
              if (typeof onExit === 'function') onExit()
            }}
            title={t('relatedStudies.compareModeOff')}
            aria-label={t('relatedStudies.compareExitAria')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 12px',
              borderRadius: 999,
              border: '1px solid rgba(100, 181, 246, 0.65)',
              background: 'rgba(30, 60, 100, 0.92)',
              color: '#e3f2fd',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.2,
              cursor: 'pointer',
              boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#64b5f6',
                boxShadow: '0 0 0 2px rgba(100,181,246,0.35)',
              }}
            />
            <span>{t('relatedStudies.compareExitChip')}</span>
            <span style={{ opacity: 0.85, fontWeight: 600 }}>✕</span>
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default CompareModeGuide
